import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ICreatePolicy, IPermission, IService } from '@shared/models/scim-admin';
import { NotificationService } from '@shared/services/notification.service';
import { forkJoin, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ScimAdminService } from '../scim-admin.service';

export interface IPermissionServiceGroup {
	service: IService;
	permissions: IPermission[];
}

@Component({
	selector: 'app-create-policy-dialog',
	standalone: false,
	templateUrl: './create-policy-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./create-policy-dialog.component.scss'],
})
export class CreatePolicyDialogComponent implements OnInit, OnDestroy {
	@Output() created = new EventEmitter<any>();
	private destroy$ = new Subject<void>();

	form: FormGroup;
	isLoading = false;
	isLoadingPermissions = true;
	permissionGroups: IPermissionServiceGroup[] = [];
	selectedPermissions = new Set<string>();

	constructor(
		public dialogRef: MatDialogRef<CreatePolicyDialogComponent>,
		private scimAdminService: ScimAdminService,
		private fb: FormBuilder,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.initializeForm();
		this.loadServicesAndPermissions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: ['', [Validators.required, Validators.maxLength(60)]],
			description: [''],
			level: ['organization', [Validators.required]],
		});
	}

	private loadServicesAndPermissions(): void {
		this.scimAdminService
			.getServices()
			.pipe(take(1))
			.subscribe({
				next: (services) => {
					if (services.length === 0) {
						this.isLoadingPermissions = false;
						return;
					}
					const permissionRequests = services.map((s) => this.scimAdminService.getServicePermissions(s.id));
					forkJoin(permissionRequests)
						.pipe(take(1))
						.subscribe({
							next: (allPermissions) => {
								this.permissionGroups = services
									.map((service, index) => ({
										service,
										permissions: allPermissions[index] || [],
									}))
									.filter((g) => g.permissions.length > 0);
								this.isLoadingPermissions = false;
							},
							error: () => {
								this.isLoadingPermissions = false;
							},
						});
				},
				error: () => {
					this.isLoadingPermissions = false;
				},
			});
	}

	togglePermission(permissionId: string): void {
		if (this.selectedPermissions.has(permissionId)) {
			this.selectedPermissions.delete(permissionId);
		} else {
			this.selectedPermissions.add(permissionId);
		}
	}

	isPermissionSelected(permissionId: string): boolean {
		return this.selectedPermissions.has(permissionId);
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.isLoading || this.form.invalid) return;

		this.isLoading = true;

		const data: ICreatePolicy = {
			name: this.form.value.name,
			description: this.form.value.description || null,
			permissions: Array.from(this.selectedPermissions),
		};

		const level = this.form.value.level;
		const request$ = level === 'organization' ? this.scimAdminService.createOrganizationPolicy(data) : this.scimAdminService.createAccountPolicy(data);

		request$.pipe(take(1)).subscribe({
			next: (createdPolicy) => {
				this.notificationService.notify(['scim-administration.policies.notifications.createSuccess.header'], ['scim-administration.policies.notifications.createSuccess.content'], 'success');
				this.created.emit(createdPolicy);
				this.dialogRef.close(createdPolicy);
			},
			error: (error) => {
				this.isLoading = false;
				this.notificationService.notify(['scim-administration.policies.notifications.createError.header'], [error?.error?.error?.message || 'scim-administration.policies.notifications.createError.content'], 'error');
			},
		});
	}
}
